/* eslint react/no-multi-comp: 0 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { defineMessages } from 'react-intl';
import { AngleDown } from '../common/fa_icons';
import RosettaFormattedMessage from '../common/rosetta_formatted_message';
import { setLocation } from '../../utils/dom';
import { sendDataLayerEvent } from '../../utils/analytics';
import {map, reject, filter, find, replace} from 'lodash';
import {
  EDITION_ID_EN_US,
  EDITION_ID_EN_GB,
  EDITION_ID_DE_DE,
  EDITION_ID_FR_FR,
  EDITION_ID_EN_AU,
  EDITION_BASENAME_EN_US,
  EDITION_BASENAME_EN_GB,
  EDITION_BASENAME_DE_DE,
  EDITION_BASENAME_FR_FR,
  EDITION_BASENAME_EN_AU,
  ENGLISH_EDITION_IDS
} from '../../constants';

defineMessages({
  edition_label_en_us: {
    id: 'edition_label_en_us',
    description: 'United States placeholder',
    defaultMessage: 'United States'
  },
  edition_label_en_gb: {
    id: 'edition_label_en_gb',
    description: 'United Kingdom placeholder',
    defaultMessage: 'United Kingdom'
  },
  edition_label_de_de: {
    id: 'edition_label_de_de',
    description: 'Germany placeholder',
    defaultMessage: 'Germany'
  },
  edition_label_fr_fr: {
    id: 'edition_label_fr_fr',
    description: 'France placeholder',
    defaultMessage: 'France'
  },
  edition_label_en_au: {
    id: 'edition_label_en_au',
    description: 'Australia placeholder',
    defaultMessage: 'Australia'
  }
});

const DEFAULT_URLS = [
  {
    edition_id: EDITION_ID_EN_US,
    url: 'https://www.refinery29.com/' + EDITION_BASENAME_EN_US
  },
  {
    edition_id: EDITION_ID_EN_GB,
    url: 'https://www.refinery29.com/' + EDITION_BASENAME_EN_GB
  },
  {
    edition_id: EDITION_ID_DE_DE,
    url: 'https://www.refinery29.com/' + EDITION_BASENAME_DE_DE
  },
  {
    edition_id: EDITION_ID_FR_FR,
    url: 'https://www.refinery29.com/' + EDITION_BASENAME_FR_FR
  },
  {
    edition_id: EDITION_ID_EN_AU,
    url: 'https://www.refinery29.com/' + EDITION_BASENAME_EN_AU
  }
];

const EDITION_ID_TO_BASENAME = {
  [EDITION_ID_EN_US]: EDITION_BASENAME_EN_US,
  [EDITION_ID_EN_GB]: EDITION_BASENAME_EN_GB,
  [EDITION_ID_DE_DE]: EDITION_BASENAME_DE_DE,
  [EDITION_ID_FR_FR]: EDITION_BASENAME_FR_FR,
  [EDITION_ID_EN_AU]: EDITION_BASENAME_EN_AU
};

export class EditionSwitchLink extends Component {
  handleClick = (event) => {
    event.preventDefault();

    const currentEdition = EDITION_ID_TO_BASENAME[this.props.currentEditionId];
    const newEdition = EDITION_ID_TO_BASENAME[this.props.editionId];

    sendDataLayerEvent('nav_item_click', {data:{navLocation: 'header', navItem: `${currentEdition}_to_${newEdition}`}}, true, this.props.url
    );
  }

  render() {
    const basename = EDITION_ID_TO_BASENAME[this.props.editionId];
    if (!basename) {
      return null;
    }
    return (
      <li>
        <a href={this.props.url} onClick={this.handleClick}>
          <RosettaFormattedMessage parentFilename={__filename}
            id={'edition_label_' + replace(basename, '-', '_')}
            description="Name of the edition"
          />
        </a>
      </li>
    );
  }
}

EditionSwitchLink.propTypes = {
  url: PropTypes.string.isRequired,
  editionId: PropTypes.number.isRequired,
  currentEditionId: PropTypes.number.isRequired
};

export default class EditionSwitch extends Component {
  state = { dropdownOpen: false }

  toggleDropdown = () => {
    this.setState({ dropdownOpen: !this.state.dropdownOpen });
  }

  handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    } else if (event.key === 'Escape') {
      this.setState({ dropdownOpen: false });
    }
  }

  handleMouseEnter = () => {
    this.setState({ dropdownOpen: true });
  }

  handleMouseLeave = () => {
    this.setState({ dropdownOpen: false });
  };

  otherEditions = () => {
    /*
     * The links we want to show in the edition switcher are
     * 1. Anything in the alternate_urls that are not
     *    the current edition
     */
    const alternateUrls = reject(
      this.props.alternate_urls,
      { edition_id: this.props.edition_data.id }
    );

    /*
     * 2. Hardcoded links to the homepages of other editions
     *    that are not represented in #1
     */
    const otherEditions = reject(
      DEFAULT_URLS,
      { edition_id: this.props.edition_data.id }
    );
    const otherEditionsMinusAlternateUrls = filter(
      otherEditions,
      ({ edition_id }) => !find(alternateUrls, { edition_id })
    );

    return [
      ...alternateUrls,
      ...otherEditionsMinusAlternateUrls
    ];
  };

  baseName = this.props.edition_data.basename === 'en-ca' ? 'en-us' : this.props.edition_data.basename;
  
  render() {
    const { dropdownOpen } = this.state;
    
    return (
      <div className="global-edition-switch"
        aria-label="Edition selector"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className="current-edition" 
          tabIndex="0"
          role="button"
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
          onKeyDown={this.handleKeyDown}
          onClick={this.toggleDropdown}
        >
          <RosettaFormattedMessage parentFilename={__filename}
            id={'edition_label_' + replace(this.baseName, '-', '_')}
            description="Name of the edition"
          />
          <AngleDown />
        </div>
        <ul className={dropdownOpen ? 'dropdown-open' : ''}>
          {ENGLISH_EDITION_IDS.includes(this.props.edition_data.id) &&
            <li className="label" aria-label="locales">Switch To</li>}
          {
            map(this.otherEditions(), ({ edition_id, url }) => (
              <EditionSwitchLink
                key={edition_id}
                url={url}
                editionId={edition_id}
                currentEditionId={this.props.edition_data.id}
              />
            ))
          }
        </ul>
      </div>
    );
  }
}

EditionSwitch.propTypes = {
  edition_data: PropTypes.object.isRequired,
  alternate_urls: PropTypes.arrayOf(
    PropTypes.shape({
      edition_id: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired
    }).isRequired
  ).isRequired
};
